(ns web3.persistence.schema
  (:require [clojure.string :as string]
            [web3.utils :as utils]
            [web3.i18n :as i]
            [web3.constants :as constants]
            [web3.forms.validation :as validation]
            [web3.components.linkify :as linkify]
            [web3.components.icons :as icons]
            [clojure.string :as str]
            [web3.icons.actions :as actions]))

;; for code related to normalizing/parsing/validating data from the db

(defn decode-phone-number
  [phone-number]
  (let [parsed-val (apply str (re-seq #"[0-9]" (or phone-number "")))
        f1         (subs parsed-val 0 3)
        f2         (subs parsed-val 3 6)
        f3         (subs parsed-val 6 (count parsed-val))
        new-val    (str f1 (when-not (empty? f2) "-")
                        f2 (when (and (not (empty? f2)) (not (empty? f3))) "-")
                        f3)]
    (->> new-val
         (take 12)
         str/join)))

(defn maybe-append-http [url]
  (when (or (some? url)
            (not (empty? url)))
   (if-not (re-find #"^http" ((fnil string/lower-case "") url))
     (str "http://" url)
     url)))

(defn asset-display-name [asset]
  (or (utils/some-str (:nickname asset))
      (utils/some-str (:tagName asset))
      (utils/some-str (:manufacturer asset))
      (utils/some-str (:modelNumber asset))
      (i/to-do "Item")))

(defn model-display-name [model]
  (let [tagName (-> model :tags first :name)
        manufacturer (-> model :manufacturer :name)
        modelNumber (-> model :number)]
    (or (utils/some-str tagName)
        (utils/some-str manufacturer)
        (utils/some-str modelNumber))))

(defn model-find-a-pro [model]
  (first (map :homeAdvisorLink (:tags model))))

(defn entity-display-name [{:keys [__typename address] :as entity}]
  (case __typename
    "Asset" (asset-display-name entity)
    "AssetGroup" (:name entity)
    "Content" (:title entity)
    "Property" (or (utils/some-str (:nickname entity))
                   (utils/some-str (:street1 entity))
                   (->> [(:locality address) (:region address)]
                        (keep (comp utils/some-str))
                        (str/join ", ")))))

(defn address->text [{:keys [street1 street2 locality region country postalCode]}]
  (->> [street1
        street2
        (str (some-> locality
                     (utils/some-str)
                     (str ", "))
             (some-> region
                     (utils/some-str)
                     (str " "))
             postalCode)
        country]
       (keep utils/some-str)
       (str/join \newline)))

(defn find-a-pro-link [asset]
  (first (for [tag (into [(:tag asset)]
                         (:tags (:model asset)))
               :let [link (:homeAdvisorLink tag)]
               :when link]
           link)))

(defn property-nickname [{:keys [nickname]
                          {city :locality} :address}]
  (or (utils/some-str nickname)
      (when city
        (i/get :property/default-nickname {:city city}))))

(defn user-nickname [{:keys [firstName lastName email]}]
  (or (when firstName
        (str firstName " " lastName))
      lastName
      email))

(defn property-display-lines [{{:keys [region locality street1 street2 postalCode]} :address
                               :as property}]
  (keep utils/some-str
        [(property-nickname property)
         street1
         street2
         (string/join ", " [locality (str region " " postalCode)])]))

(def file-collections
  [:files/media
   ;; :files/manuals
   :files/receipts
   :files/documents
   :files/notes
   :files/items])

(defn file-collection-icon [key]
  (let [key (get {:files/items :group/everything-else} key key)]
    (icons/get key)))

(defn file-collection-key
  [s]
  (if (keyword? s)
    s
    (->> file-collections
         (filter (comp (partial = s)
                       name))
         first)))

(def collection-categories
  {:files/media #{"image" "video"}
   :files/manuals #{"video" "document" "note"}
   :files/receipts #{"receipt"}
   :files/documents #{"document"}
   :files/notes #{"note"}})

(def category->file-collection
  (reduce-kv
   (fn [m collection categories]
     (reduce #(assoc %1 %2 collection) m categories))
   {}
   (dissoc collection-categories :files/manuals)))

(def file-exts
  (let [image-ext #{"png" "jpg" "jpeg" "gif"}]
    {"image" image-ext
     "video" #{"mp4" "mov" "qt"}
     "receipt" (conj image-ext "pdf")
     "document" (into #{"pdf"} image-ext)}))

(def dropify-file-exts
  "File extensions formatted for passing to dropify"
  (utils/map-vals (partial str/join " ") file-exts))


(defn ext-category
  "Returns the content category for a given file extension."
  ;; TODO
  ;; not sure if this is possible to determine -- receipt/document/image overlap?
  [ext]
  (->> file-exts
       (filter #(get (second %) ext))
       (ffirst)))

(def file-collection-extensions-map
  "A map of {<file collection keyword>, <allowed file extensions string>}"
  (utils/map-vals (fn [categories]
                    (->> (mapcat file-exts categories)
                         (str/join " ")))
                  collection-categories))

(def file-collection-extensions
  (comp
   file-collection-extensions-map
   file-collection-key))

(def region-options
  (list
   [:option {:key 1 :disabled true}]
   [:optgroup {:label (i/get :StreetAddress/country.us)
               :key :us}
    (for [[code label] constants/states]
      [:option {:key code
                :value code} label])]

   [:optgroup {:label (i/get :StreetAddress/country.ca)
               :key :ca}
    (for [[code label] constants/provinces]
      [:option {:key code
                :value code} label])]))

(def country-options
  (for [[code label] constants/countries]
    [:option {:key code
              :value code} label]))

(defn wrapped-phone-number [num]
  (when num
    [:a.text-blue {:href (str "tel:" num)} (decode-phone-number num)]))

(defn image-upload [& [props]]
  (merge {:kind :file-upload
          :data-allowed-file-extensions
          (get dropify-file-exts "image")}
         props))

(def form-fields
  {:Property/nickname {:kind :text-input
                       :label (str (i/get :i/property) " " (i/get :i/nickname))
                       :auto-capitalize "on"
                       :helptext "This is how your property will be referenced in HomeServe."
                       :auto-complete "off"}

   :StreetAddress/country {:kind :select
                           :children country-options
                           :default "USA"
                           :validators [:required]
                           :auto-complete "country"}
   :StreetAddress/region {:kind :select
                          :children region-options
                          :validators [:required]
                          :auto-complete "address-level1"}
   :StreetAddress/locality {:kind :text-input
                            :auto-complete "address-level2"
                            :validators [:required]}
   :StreetAddress/street1 {:kind :text-input
                           :auto-complete "address-line1"}
   :StreetAddress/street2 {:kind :text-input
                           :auto-complete "address-line2"}
   :StreetAddress/postalCode {:kind :text-input
                              :validators [:required :postal-code/north-america]
                              :encoding {:encode str/upper-case
                                         :decode str/upper-case}
                              :auto-complete "postal-code"}

   :User/firstName {:kind :text-input
                    :auto-complete "given-name"}
   :User/lastName {:kind :text-input
                   :auto-complete "family-name"}
   :User/email {:kind :text-input
                :auto-correct "off"
                :auto-capitalize "off"
                :spell-check "false"
                :validators [:required]
                :auto-complete "email"}
   :User/password {:kind :text-input
                   :type "password"
                   :validators [:required]}
   :User/current-password {:kind :text-input
                           :type "password"
                           :validators [:required]}
   :User/new-password {:kind :text-input
                       :type "password"
                       :validators [:required [:min-length 6]]}
   :User/confirm-password {:kind :text-input
                           :type "password"
                           :validators [:required]}
   :Asset/manufacturer {:kind :text-search
                        :entity-type :Manufacturer
                        :helptext (i/to-do "Enter the brand name here. See a match? Select it.")
                        :validators [[:min-length 2]]
                        :auto-correct "off"
                        :auto-capitalize "on"
                        :auto-complete "off"}
   :Asset/modelNumber {:kind :model-search
                       :depends-on :Asset/manufacturer
                       :entity-type :Model
                       :validators [[:min-length 2]]
                       :auto-correct "off"
                       :auto-capitalize "off"
                       :auto-complete "off"
                       :helptext (i/to-do "Enter the model number (eg. \"iPhone 7\"). See a match? Select it.")}
   :Asset/nameplateLink (image-upload {:validators [:required]})
   :Asset/imageLink (-> (image-upload)
                        (assoc :label (i/get :i/photo)))
   :User/personImageLink (image-upload {:mobile-hint? false})
   :User/companyImageLink (image-upload {:mobile-hint? false})
   :User/proReferralLinkPath {:kind :text-input
                              :label (i/to-do "Your Phone Number")
                              :validators [:required
                                           [:min-length 10]
                                           [:max-length 10]]}
   :Asset/tagName {:kind :text-search
                   :validators [[:min-length 2]]
                   :auto-complete "off"
                   :helptext (i/to-do "Enter the type of item (like \"phone\"). See a match? Select it.")
                   :entity-type :Tag}
   :Asset/nickname {:kind :text-input
                    :label (str (i/get :i/Asset) " " (i/get :i/nickname))
                    :auto-capitalize "on"
                    :helptext (i/to-do "This is how your item will be referenced in HomeServe.")}
   :Asset/serialNumber {:kind :text-input}
   :Asset/assetGroupId {:kind :parent-selector
                        :validators [:required]
                        :parent-context {:allowed-types #{"AssetGroup"}}
                        :label (i/get :i/add-to)}
   :Asset/transcriptionComment {:label (i/get :i/comments)
                                :kind :text-input
                                :multi-line? true
                                :helptext-persistent? true
                                :helptext (i/get :asset/transcription-comment-helptext)}
   :ServiceProvider/companyName {:kind :text-input}
   :ServiceProvider/email {:kind :text-input
                           :view-as linkify/linkify
                           :auto-correct "off"
                           :auto-capitalize "off"
                           :spell-check "false"}
   :ServiceProvider/firstName {:kind :text-input}
   :ServiceProvider/lastName {:kind :text-input
                              :dense? true}
   :ServiceProvider/homePhone {:kind :text-input
                               :view-as wrapped-phone-number
                               :encoding {:decode decode-phone-number}
                               :label-icon actions/phone
                               :dense? true}
   :ServiceProvider/iphonePhone {:kind :text-input
                                 :view-as wrapped-phone-number
                                 :encoding {:decode decode-phone-number}
                                 :label-icon actions/phone
                                 :dense? true}
   :ServiceProvider/mobilePhone {:kind :text-input
                                 :view-as wrapped-phone-number
                                 :encoding {:decode decode-phone-number}
                                 :label-icon actions/phone
                                 :dense? true}
   :ServiceProvider/officePhone {:kind :text-input
                                 :view-as wrapped-phone-number
                                 :encoding {:decode decode-phone-number}
                                 :label-icon actions/phone
                                 :dense? true}
   :ServiceProvider/otherPhone {:kind :text-input
                                :view-as wrapped-phone-number
                                :encoding {:decode decode-phone-number}
                                :label-icon actions/phone
                                :dense? true}
   :ServiceProvider/note {:kind :text-input
                          :multi-line? true
                          :view-as linkify/linkify}
   :ServiceProvider/professionName {:kind :text-input}
   :ServiceProvider/website {:kind :text-input
                             :view-as linkify/linkify
                             :encoding {:decode maybe-append-http}
                             :auto-correct "off"
                             :auto-capitalize "off"
                             :spell-check "false"}

   :Content/title {:kind :text-input
                   :auto-capitalize "on"}
   :Note/title {:kind :text-input
                :auto-capitalize "on"} ;;TODO handle this more idiomatically once the direction is more clear because it is literally just turning on :Content/title's required validator
   :Content/contentedId {:kind :parent-selector
                         :select-asset? true
                         :validators [:required]
                         :label (i/get :i/add-to)}
   :Content/description {:kind :text-input
                         :multi-line? true}
   :Content/body {:kind :text-input
                  :encoding :link-data
                  :multi-line? true
                  :view-as linkify/linkify
                  :validators [:required]}
   :Content/group {:kind :select}
   :Content/asset {:kind :select}
   :Content/link {:kind :file-upload
                  :validators [:required]
                  :label (i/get :i/upload-file)}})

(defn validate-paths
  "Validate paths in a data structure.

  Each path in `paths` should be a either a [field-name, path] pair, or a namespaced keyword
   like `:ServiceProvider/note` which will be expended to [:ServiceProvider/note [:note]].

   where `field-name` corresponds to a field registered in the schema, and `path` corresponds
   to the path in `data` which should be inspected."
  ([data] (validate-paths (keys data) data))
  ([paths data] (validate-paths paths data {}))
  ([paths data validators]
   ;; TODO
   ;; should we be using a recursive `map-keys` here? current version is flat
   (let [data (utils/map-keys utils/simplify-keyword data)]
     (->> paths
          (reduce (fn [m keypath]
                    (let [[name path] (if (keyword? keypath) [keypath [keypath]]
                                                             keypath)
                          schema-data (get form-fields name)
                          additional-validators (get-in validators path)
                          value (get-in data (mapv utils/simplify-keyword path))]
                      (assoc m name (-> schema-data
                                        (assoc :value value)
                                        (update :info assoc :data data)
                                        (cond-> additional-validators
                                                (update :validators (comp distinct into) additional-validators)))))) {})
          (validation/validate-map)))))

(def service-provider-fields [:ServiceProvider/firstName
                              :ServiceProvider/lastName
                              :ServiceProvider/companyName
                              :ServiceProvider/email
                              :ServiceProvider/homePhone
                              :ServiceProvider/iphonePhone
                              :ServiceProvider/mobilePhone
                              :ServiceProvider/officePhone
                              :ServiceProvider/otherPhone
                              :ServiceProvider/note
                              :ServiceProvider/professionName
                              :ServiceProvider/website])

(def validate-service-provider
  (partial validate-paths service-provider-fields))

(def validate-property
  (partial validate-paths [[:Property/nickname [:nickname]]
                           [:StreetAddress/country [:address :country]]
                           [:StreetAddress/region [:address :region]]
                           [:StreetAddress/locality [:address :locality]]
                           [:StreetAddress/street1 [:address :street1]]
                           [:StreetAddress/street2 [:address :street2]]
                           [:StreetAddress/postalCode [:address :postalCode]]]))
